import SeoMetaHead from '@components/common/seo-meta-head';
import { settingService } from '@services/setting.service';
import { useEffect } from 'react';
import { authService } from '@services/auth.service';
import Router, { useRouter } from 'next/router';
import LoginForm from '@components/auth/login-form';
import Logo from '@components/common/base/logo';
import nextCookie from 'next-cookies';
import style from './login.module.scss';
import LoginLeftColumn from './LoginLeftColumn';

type Props = {
  title: string;
  metaKeywords: string;
  metaDescription: string;
  loginPlaceholderImage: string;
  googleLoginClientId: string;
  googleLoginEnabled: boolean;
  twitterLoginEnabled: boolean;
};

function Login({
  title,
  metaKeywords,
  metaDescription,
  loginPlaceholderImage,
  googleLoginClientId,
  googleLoginEnabled,
  twitterLoginEnabled
}: Props) {
  const router = useRouter();

  const handleLoggedInRedirect = async () => {
    const token = authService.getToken();
    if (!token) return;
    try {
      Router.push('/home');
    } catch {
      authService.removeToken();
    }
  };

  useEffect(() => {
    if (router?.query?.redirect) {
      localStorage.setItem('redirect', router.query.redirect.toString());
    }
    handleLoggedInRedirect();
  }, [router]);

  return (
    <>
      <SeoMetaHead
        pageTitle={title || 'Login'}
        keywords={metaKeywords}
        description={metaDescription}
      />
      {/* Remove default layout padding */}
      <style jsx global>
        {`
          .main-content {
            padding: 0 !important;
            align-items: center !important;
          }
          @media (max-width: 768px) {
            .main-content {
              align-items: stretch !important;
            }
          }
        `}
      </style>

      <div className="login-wrapper">
        <div className={style['login-box']}>
          {/* Left / top: blue hero */}
          <LoginLeftColumn />

          {/* Right / bottom: form */}
          <div className={style['content-right']}>
            <div className={style.logo}>
              <Logo />
            </div>
            <LoginForm />
          </div>
        </div>
      </div>

      <style jsx>
        {`
    .login-wrapper {
      flex: 1 1 auto;
      display: flex;
      align-items: stretch;
      justify-content: flex-start;
      width: 100%;
      min-height: 100vh;
    }

    @media (max-width: 768px) {
      .login-wrapper {
        display: block;
        min-height: auto;
      }
    }
  `}
      </style>
    </>
  );
}

Login.authenticate = false;
Login.layout = 'public';

export const getServerSideProps = async (ctx) => {
  const { token } = nextCookie(ctx);
  if (token) {
    return {
      redirect: {
        destination: '/home',
        permanent: false
      }
    };
  }

  const meta = await settingService.valueByKeys([
    'homeTitle',
    'homeMetaKeywords',
    'homeMetaDescription',
    'loginPlaceholderImage',
    'googleLoginClientId',
    'googleLoginEnabled',
    'twitterLoginEnabled'
  ]);

  return {
    props: {
      title: meta?.homeTitle || '',
      metaKeywords: meta?.homeMetaKeywords || '',
      metaDescription: meta?.homeMetaDescription || '',
      loginPlaceholderImage: meta?.loginPlaceholderImage || '',
      googleLoginClientId: meta?.googleLoginClientId || '',
      googleLoginEnabled: meta?.googleLoginEnabled || false,
      twitterLoginEnabled: meta?.twitterLoginEnabled || false
    }
  };
};

export default Login;
